/**
 * Authentication Context
 *
 * Provides global authentication state and methods for the application.
 * Handles login, logout, token refresh, and user session management.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios, { AxiosError } from 'axios';
import { LEGACY_ROLE_PERMISSIONS } from '../../core/config/hubRegistry';
import type { User, UserRole, UserHub, HubPermission } from '../../types';

// Auth context value type
interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  userHubs: UserHub[];
  hubPermissions: Record<string, HubPermission[]>;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<string>;
  fetchUserHubs: () => Promise<UserHub[]>;
  fetchHubPermissions: (hubKey: string) => Promise<HubPermission[]>;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  hasPermission: (permission: string | string[]) => boolean;
  hasHubAccess: (hubKey: string) => boolean;
  hasHubPermission: (hubKey: string, permissionKey: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Use relative URLs to go through Vite proxy (configured in vite.config.ts)
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Props for AuthProvider
interface AuthProviderProps {
  children: ReactNode;
}

// Role permissions mapping
const rolePermissions: Record<UserRole, string[]> = {
  admin: ['read', 'write', 'delete', 'manage_users', 'configure_settings', 'view_audit_logs', 'manage_reports', 'approve_schedules', 'override_schedules'],
  operator: ['read', 'write', 'create_schedules', 'modify_schedules', 'run_simulations', 'generate_reports'],
  viewer: ['read', 'view_schedules', 'view_reports', 'view_dashboard']
};

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userHubs, setUserHubs] = useState<UserHub[]>([]); // User's accessible hubs
  const [hubPermissions, setHubPermissions] = useState<Record<string, HubPermission[]>>({}); // Permissions per hub

  /**
   * Login with email and password
   */
  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setError(null);
      setLoading(true);

      const response = await axios.post<{
        accessToken: string;
        refreshToken: string;
        user: User;
      }>(`${API_BASE_URL}/api/auth/login`, {
        email,
        password
      });

      const { accessToken, refreshToken, user: userData } = response.data;

      // Store tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Set user
      setUser(userData);

      return { success: true };
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      const errorMessage = axiosError.response?.data?.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout and clear session
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        // Call backend logout to invalidate session
        await axios.post(
          `${API_BASE_URL}/api/auth/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }

      // Clear local storage and state first
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);

      // Get Azure AD logout URL and redirect to fully sign out
      try {
        const response = await axios.get<{ logoutUrl: string }>(`${API_BASE_URL}/api/auth/oauth/logout`);
        if (response.data.logoutUrl) {
          // Redirect to Azure AD logout to clear browser session
          window.location.href = response.data.logoutUrl;
          return;
        }
      } catch (oauthErr) {
        // If OAuth logout fails (e.g., demo mode), just redirect to login
        console.log('OAuth logout not available, using standard logout');
      }

      // Fallback: redirect to login page
      window.location.href = '/login';
    } catch (err) {
      console.error('Logout error:', err);
      // Even on error, clear local storage and redirect
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      window.location.href = '/login';
    }
  }, []);

  /**
   * Refresh access token
   */
  const refreshAccessToken = useCallback(async (): Promise<string> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post<{ accessToken: string }>(`${API_BASE_URL}/api/auth/refresh`, {
        refreshToken
      });

      const { accessToken } = response.data;
      localStorage.setItem('accessToken', accessToken);

      return accessToken;
    } catch (err) {
      console.error('Token refresh failed:', err);
      // If refresh fails, logout
      await logout();
      throw err;
    }
  }, [logout]);

  /**
   * Get current user info
   */
  const getCurrentUser = useCallback(async (): Promise<User | null> => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return null;
      }

      const response = await axios.get<{ user: User }>(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data.user;
    } catch (err) {
      console.error('Get current user failed:', err);
      return null;
    }
  }, []);

  /**
   * Fetch user's accessible hubs
   */
  const fetchUserHubs = useCallback(async (): Promise<UserHub[]> => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return [];
      }

      const response = await axios.get<{ hubs: UserHub[] }>(`${API_BASE_URL}/api/auth/user-hubs`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const hubs = response.data.hubs || [];
      setUserHubs(hubs);
      return hubs;
    } catch (err) {
      console.error('Failed to fetch user hubs:', err);
      return [];
    }
  }, []);

  /**
   * Fetch user's permissions for a specific hub
   */
  const fetchHubPermissions = useCallback(async (hubKey: string): Promise<HubPermission[]> => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return [];
      }

      const response = await axios.get<{ permissions: HubPermission[] }>(
        `${API_BASE_URL}/api/auth/hub-permissions/${hubKey}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const permissions = response.data.permissions || [];
      setHubPermissions(prev => ({
        ...prev,
        [hubKey]: permissions
      }));
      return permissions;
    } catch (err) {
      console.error(`Failed to fetch permissions for hub ${hubKey}:`, err);
      return [];
    }
  }, []);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          setLoading(false);
          return;
        }

        // Get current user info
        const userData = await getCurrentUser();
        if (userData) {
          setUser(userData);
          // Fetch user's accessible hubs
          await fetchUserHubs();
        } else {
          // Token is invalid, clear it
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      } catch (err) {
        console.error('Auth initialization failed:', err);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [getCurrentUser, fetchUserHubs]);

  /**
   * Check if user has specific role
   */
  const hasRole = useCallback((role: UserRole | UserRole[]): boolean => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  }, [user]);

  /**
   * Check if user has specific permission
   */
  const hasPermission = useCallback((permission: string | string[]): boolean => {
    if (!user) return false;

    const permissions = rolePermissions[user.role] || [];

    if (Array.isArray(permission)) {
      return permission.some(p => permissions.includes(p));
    }

    return permissions.includes(permission);
  }, [user]);

  /**
   * Check if user has access to a specific hub
   */
  const hasHubAccess = useCallback((hubKey: string): boolean => {
    if (!user) return false;

    // Admin users have access to all hubs
    if (user.role === 'admin') return true;

    // Check if hub is in user's accessible hubs list (from database)
    if (userHubs.length > 0) {
      return userHubs.some(hub => hub.hub_key === hubKey);
    }

    // Fallback to legacy role-based permissions if no database entries
    const legacyHubs = LEGACY_ROLE_PERMISSIONS[user.role as keyof typeof LEGACY_ROLE_PERMISSIONS] || [];
    return legacyHubs.includes(hubKey);
  }, [user, userHubs]);

  /**
   * Check if user has a specific permission within a hub
   */
  const hasHubPermission = useCallback((hubKey: string, permissionKey: string | string[]): boolean => {
    if (!user || !hasHubAccess(hubKey)) return false;

    const permissions = hubPermissions[hubKey] || [];

    if (Array.isArray(permissionKey)) {
      return permissionKey.some(p => permissions.some(perm => perm.permission_key === p));
    }

    return permissions.some(perm => perm.permission_key === permissionKey);
  }, [user, hubPermissions, hasHubAccess]);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    userHubs,
    hubPermissions,
    login,
    logout,
    refreshAccessToken,
    fetchUserHubs,
    fetchHubPermissions,
    hasRole,
    hasPermission,
    hasHubAccess,
    hasHubPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
